import { getRouteApi } from '@tanstack/react-router';
import { Clock, Wrench } from 'lucide-react';
import { useId, useMemo, useState, type FormEvent } from 'react';

import { BarraFiltros, type ChipFiltro } from '@/componentes/BarraFiltros';
import { Botao } from '@/componentes/Botao';
import { CabecalhoPagina } from '@/componentes/CabecalhoPagina';
import { Campo, CampoSelecao } from '@/componentes/Campo';
import { CardIndicador } from '@/componentes/CardIndicador';
import { ControleCarregando } from '@/componentes/Esqueleto';
import { EstadoVazio } from '@/componentes/EstadoVazio';
import { FaixaIndicadores } from '@/componentes/FaixaIndicadores';
import { Janela } from '@/componentes/JanelaDeAcao';
import { BotaoDoRaro } from '@/componentes/LinhaDoPreenchido';
import { ListaDeEscolha } from '@/componentes/ListaDeEscolha';
import { Medida } from '@/componentes/Medida';
import { SemPermissao } from '@/componentes/SemPermissao';
import { RodapeTabela, Tabela, type Coluna } from '@/componentes/Tabela';
import { HistoriaNaMesa } from '@/historias/mesa';
import { diaDaOperacao } from '@/lib/dia-da-operacao';
import { formatarAteDuasCasas, formatarData, formatarDecimal, formatarInteiro, interpretarNumero } from '@/lib/formato';
import { perfilAPedir } from '@/lib/perfil-da-licenca';
import { primeiraMaiuscula } from '@/lib/texto';
import { EtiquetaDaUltimaLeitura } from '@/modulos/combustivel/EtiquetaDaUltimaLeitura';
import { AgendaDeParadas } from '@/modulos/oficina/AgendaDeParadas';
import { useAbrirOrdem } from '@/modulos/oficina/consultas';
import { numeroDaOrdem } from '@/modulos/oficina/ordem';
import { linhaCurtaDaParada } from '@/modulos/oficina/parada';
import { FormularioDaParada } from '@/modulos/oficina/ParadaProgramada';
import { useSessao } from '@/modulos/sessao/consultas';

import type { VencimentoDaApi } from './api';
import {
  OPCOES_POR_PAGINA,
  ORDEM_DOS_FILTROS,
  PADRAO_POR_PAGINA,
  ehEstadoDoVencimento as ehEstado,
  montarBuscaDaManutencao,
  type FiltroDeEstado,
} from './busca';
import { explicarFalha, useRegistrarRealizacao, useRealizacoes, useVencimentos } from './consultas';
import { FormularioDePlano, PainelDoPlano, PlanosDaManutencao } from './PlanosDaManutencao';
import { leituraSugerida } from './realizacao';
import {
  baseCurtaDaPrevisao,
  baseDaPrevisao,
  contarFila,
  corDoEstado,
  dataDoDia,
  MEDIDOR_DO_GATILHO,
  ROTULO_DA_CONFIANCA,
  ROTULO_DO_ESTADO,
  textoDoFaltam,
  textoDoIntervalo,
  UNIDADE_DO_GATILHO,
} from './vencimento';
import { useOpcoesComVisualDaFrota } from '@/modulos/equipamentos/opcoesComVisual';

/**
 * Manutenção planejada — o plano preventivo e o vencimento de cada máquina.
 * Módulo 05 do mapa funcional v6; passo 5 da sequência do ciclo 1.
 *
 * ## A fila é o alerta
 *
 * O guia pede "prever vencimentos e gerar alertas". Não há e-mail nem aviso
 * empurrado no sistema — escolher o canal é decisão em aberto. O alerta, hoje,
 * é esta fila: o que venceu primeiro, o que vence logo em seguida, e ao lado de
 * cada previsão a leitura em que ela se apoia e a data dessa leitura. O v6
 * escreve o risco por extenso: "previsão falsa por horímetro desatualizado".
 *
 * ## O que a tela não faz, e diz
 *
 * Não abre O.S. e não lista peças: a oficina é o passo 6 e o estoque é o 7. O
 * ponto de partida de cada tarefa, por enquanto, é registrado à mão com a
 * evidência escrita — é a carga inicial do piloto.
 */

/* O vocabulario do filtro e a paginacao moram em `busca.ts` e em
   `lib/paginacao-na-url`, junto do validador da URL: duas listas de estado
   divergiriam, e a do validador passaria a recusar o que a barra oferece. */

function chaveDe(v: VencimentoDaApi): string {
  return `${v.equipamentoId}-${v.tarefaId}`;
}

/** O retorno de "Abrir O.S." mora NA LINHA que o pediu (R01, p.27). */
interface RetornoDaLinha {
  readonly chave: string;
  readonly texto: string;
  readonly falhou: boolean;
}

/**
 * Número com unidade, na cor do alerta quando há alerta (R02, p.31: números
 * de alerta em vermelho, próximos em âmbar). A unidade continua cinza e sem
 * negrito (p.12). Sem cor, é a `Medida` de sempre.
 */
function MedidaNaCor({
  valor,
  unidade,
  cor,
}: {
  readonly valor: string;
  readonly unidade: string;
  readonly cor: string | null;
}) {
  if (cor === null) {
    return (
      <span className="whitespace-nowrap">
        <Medida valor={valor} unidade={unidade} />
      </span>
    );
  }
  return (
    <span className={`numero whitespace-nowrap ${cor}`}>
      {valor}
      <span className="text-tinta-secundaria">{` ${unidade}`}</span>
    </span>
  );
}

/**
 * A linha de APOIO da célula (p.18: 12 px, apagada) ocupa a largura que a
 * coluna TIVER e corta ali, com reticências: `w-0 min-w-full` a tira da conta
 * de largura da tabela — quem disputa a largura é a linha principal. O texto
 * inteiro vai no `title`. No cartão (abaixo de 768 px, R01) não há disputa de
 * coluna, e ela desce a até duas linhas em vez de cortar.
 */
function Apoio({ texto, completo = texto }: { readonly texto: string; readonly completo?: string }) {
  return (
    <span
      className="txt-apoio block w-0 min-w-full truncate text-tinta-secundaria max-[768px]:line-clamp-2 max-[768px]:w-auto max-[768px]:whitespace-normal"
      title={completo}
    >
      {texto}
    </span>
  );
}

const rota = getRouteApi('/manutencao');

export function PaginaManutencao() {
  const sessao = useSessao();
  const permissoes = sessao.data?.permissoes ?? [];
  const pode = (codigo: string) => permissoes.includes(codigo);

  const idDaMaquina = useId();
  const vencimentos = useVencimentos();

  const [montando, setMontando] = useState(false);
  const [planoAberto, setPlanoAberto] = useState<string | null>(null);
  /* OS FILTROS MORAM NA URL (p.16), e nao em `useState`: "olha o que venceu
     nessa maquina" e um link que se manda, e recarregar nao pode voltar a
     lista inteira. O vocabulario e o validador estao em `busca.ts`. */
  const busca = rota.useSearch();
  const navegar = rota.useNavigate();
  const estado: FiltroDeEstado = busca.estado ?? '';
  const maquina = busca.maquina ?? '';
  const pagina = busca.pagina ?? 1;
  const porPagina = busca.porPagina ?? PADRAO_POR_PAGINA;
  const [registrando, setRegistrando] = useState<VencimentoDaApi | null>(null);
  /* A O.S. aberta cuja parada se combina com a operação (0089). */
  const [programando, setProgramando] = useState<VencimentoDaApi | null>(null);
  const abrirOrdem = useAbrirOrdem();
  const [retorno, setRetorno] = useState<RetornoDaLinha | null>(null);

  const todos = useMemo(() => vencimentos.data ?? [], [vencimentos.data]);
  const fila = useMemo(() => contarFila(todos), [todos]);
  const comVisualDaFrota = useOpcoesComVisualDaFrota();
  const maquinas = useMemo(() => {
    const vistas = new Map<string, string>();
    for (const v of todos) vistas.set(v.equipamentoId, v.equipamento);
    return [...vistas.entries()].sort((x, y) => x[1].localeCompare(y[1]));
  }, [todos]);
  const linhas = useMemo(
    () => todos.filter((v) => (estado === '' || v.estado === estado) && (maquina === '' || v.equipamentoId === maquina)),
    [todos, estado, maquina],
  );

  const paginas = Math.max(Math.ceil(linhas.length / porPagina), 1);
  const paginaSegura = Math.min(pagina, paginas);
  const inicio = (paginaSegura - 1) * porPagina;
  const visiveis = linhas.slice(inicio, inicio + porPagina);
  const semPontoNaLista = linhas.filter((v) => v.estado === 'sem_ponto_de_partida').length;

  /* Trocar filtro volta para a primeira página: ficar na página 3 depois de
     filtrar mostra tela vazia e parece defeito.
     Trocar o filtro e NAVEGAR, nao mexer em estado: assim o botao de voltar do
     navegador desfaz a troca, que e o que a pessoa espera dele. */
  function irPara(proximo: {
    readonly estado?: FiltroDeEstado;
    readonly maquina?: string;
    readonly pagina?: number;
    readonly porPagina?: number;
  }) {
    void navegar({
      search: montarBuscaDaManutencao({
        estado: proximo.estado ?? estado,
        maquina: proximo.maquina ?? maquina,
        pagina: proximo.pagina ?? 1,
        porPagina: proximo.porPagina ?? porPagina,
      }),
    });
  }

  function filtrar(novoEstado: FiltroDeEstado, novaMaquina: string) {
    irPara({ estado: novoEstado, maquina: novaMaquina, pagina: 1 });
  }

  const nomeDaMaquina = maquinas.find(([id]) => id === maquina)?.[1] ?? maquina;
  const chips: readonly ChipFiltro[] = [
    ...(estado
      ? [
          {
            id: 'estado',
            rotulo: `Estado: ${ROTULO_DO_ESTADO[estado]}`,
            aoRemover: () => {
              filtrar('', maquina);
            },
          },
        ]
      : []),
    ...(maquina
      ? [
          {
            id: 'maquina',
            rotulo: `Equipamento: ${nomeDaMaquina}`,
            aoRemover: () => {
              filtrar(estado, '');
            },
          },
        ]
      : []),
  ];

  function abrirOrdemDaLinha(v: VencimentoDaApi) {
    const chave = chaveDe(v);
    setRetorno(null);
    abrirOrdem.mutate(
      {
        equipamentoId: v.equipamentoId,
        tipo: 'preventiva',
        prioridade: v.estado === 'vencida' ? 2 : 4,
        sintoma: `Preventiva: ${v.descricao} (${v.plano})`,
        maquinaParada: false,
        tarefaIds: [v.tarefaId],
      },
      {
        onSuccess: (r) => {
          setRetorno({
            chave,
            texto: `${numeroDaOrdem(r.numero)} aberta. Libere em Ordens de serviço.`,
            falhou: false,
          });
        },
        onError: (falha) => {
          const f = explicarFalha(falha);
          setRetorno({ chave, texto: `${primeiraMaiuscula(f.erro)}. ${f.comoResolver}`, falhou: true });
        },
      },
    );
  }

  const podeAgir = pode('realizacao.registrar') || pode('os.abrir');

  /* A ORDEM DAS COLUNAS é a da leitura (R01, p.28): identidade, o que é, e o
     ESTADO logo em seguida — é o dado principal da linha, e era o primeiro a
     sair do quadro quando a tabela não cabia. Depois o vencimento, a previsão
     e, por último, a leitura atual, que é secundária: sai abaixo de 1280 px, e
     a previsão já diz quanto falta. */
  const COLUNAS: readonly Coluna<VencimentoDaApi>[] = [
    {
      chave: 'equipamento',
      rotulo: 'Equipamento',
      /* Código em monoespaçada, para comparar na vertical (p.18). */
      celula: (v) => <span className="codigo whitespace-nowrap text-tinta">{v.equipamento}</span>,
    },
    {
      chave: 'tarefa',
      rotulo: 'Tarefa',
      /* NOME LONGO VAI A ATÉ DUAS LINHAS (p.18: o que não quebra é código e
         número), quebrando entre palavras. O piso de 160 px segura a tarefa
         em duas linhas, e não numa tira de uma palavra por linha quando o
         quadro aperta; o `line-clamp` e o `title` são a rede de um texto fora
         de medida. */
      celula: (v) => (
        <span className="flex min-w-40 flex-col">
          <span className="txt-corpo line-clamp-2 whitespace-normal text-pretty text-tinta" title={v.descricao}>
            {v.descricao}
          </span>
          {/* Segunda linha curta, só o dado (p.18). O plano inteiro está na
              lista de planos, abaixo, e no painel dele. */}
          <Apoio texto={`${textoDoIntervalo(v.gatilho, v.intervalo)} · ${v.plano}`} />
        </span>
      ),
    },
    {
      chave: 'estado',
      rotulo: 'Estado',
      alinhamento: 'status',
      celula: (v) => (
        <span className="flex flex-col">
          {/* RÓTULO ESCRITO (regra 2). Nenhuma cor carrega sozinha a informação. */}
          <span
            className={`txt-corpo whitespace-nowrap ${corDoEstado(v.estado) ?? 'text-tinta'} ${v.estado === 'vencida' ? 'font-semibold' : ''}`}
          >
            {ROTULO_DO_ESTADO[v.estado]}
          </span>
          {v.confianca ? (
            <span className="txt-apoio whitespace-nowrap text-tinta-secundaria">{ROTULO_DA_CONFIANCA[v.confianca]}</span>
          ) : null}
          {/* A O.S. ABERTA desta tarefa e a parada combinada (0089): separa
              "vencida e ninguém mexeu" de "vencida e a máquina sai quinta". */}
          {v.ordemAberta ? (
            <span className="txt-apoio whitespace-nowrap text-tinta-secundaria">
              {`${numeroDaOrdem(v.ordemAberta.numero)} · ${
                v.ordemAberta.paradaProgramada
                  ? linhaCurtaDaParada(new Date(v.ordemAberta.paradaProgramada.de), new Date(v.ordemAberta.paradaProgramada.ate))
                  : 'sem data combinada'
              }`}
            </span>
          ) : null}
          {/* O retorno de "Abrir O.S." fica NA LINHA que o pediu (R01, p.27),
              e à vista: as ações da linha só aparecem no hover. */}
          {retorno?.chave === chaveDe(v) ? (
            <span
              role={retorno.falhou ? 'alert' : 'status'}
              className={`txt-apoio whitespace-normal ${retorno.falhou ? 'text-parada' : 'text-tinta-secundaria'}`}
            >
              {retorno.texto}
            </span>
          ) : null}
        </span>
      ),
    },
    {
      chave: 'vence',
      rotulo: 'Vence em',
      historia: 'COL-MNT-02',
      alinhamento: 'numero',
      celula: (v) => {
        /* R02: vencida em vermelho, próxima em âmbar — a data do calendário e
           também a leitura do horímetro ou do odômetro. */
        const cor = corDoEstado(v.estado);
        if (v.gatilho === 'dias') {
          return v.previstaPara ? (
            <span className={`numero whitespace-nowrap ${cor ?? 'text-tinta'}`}>{formatarData(dataDoDia(v.previstaPara))}</span>
          ) : (
            <span className="txt-apoio text-tinta-secundaria">—</span>
          );
        }
        return v.venceNaLeitura === null ? (
          <span className="txt-apoio text-tinta-secundaria">—</span>
        ) : (
          <MedidaNaCor valor={formatarDecimal(v.venceNaLeitura, 1)} unidade={UNIDADE_DO_GATILHO[v.gatilho]} cor={cor} />
        );
      },
    },
    {
      chave: 'previsao',
      rotulo: 'Previsão',
      historia: 'NUM-MNT-03',
      celula: (v) => {
        const faltam = textoDoFaltam(v);
        const data = v.previstaPara && v.gatilho !== 'dias' ? formatarData(dataDoDia(v.previstaPara)) : null;
        const cor = corDoEstado(v.estado);
        const base = baseCurtaDaPrevisao(v);
        return (
          /* A DATA PRIMEIRO, e sem "por volta de": o cabeçalho já diz que é
             previsão, e a confiança está no Estado. A linha pode descer em
             duas, sempre entre as partes e nunca no meio de "faltam 35,0 h"
             ou da data — com a caixa alta, a frase longa passava do quadro
             com o painel aberto. */
          <span className="flex flex-col">
            <span className="txt-corpo whitespace-normal text-tinta">
              {faltam === null && data === null ? (
                'Sem previsão'
              ) : (
                <>
                  {data ? <span className={`whitespace-nowrap ${cor ?? ''}`}>{data}</span> : null}
                  {faltam && data ? ' · ' : null}
                  {faltam ? <span className="whitespace-nowrap">{faltam}</span> : null}
                </>
              )}
            </span>
            {/* A BASE DA PREVISÃO: o v6 manda mostrar a data da última leitura,
                e é ela que separa previsão de palpite. Na célula, curta — só o
                dado (p.18); a frase inteira fica no `title`. Sem ponto de
                partida não há base — a instrução para ele está uma vez só,
                acima da lista. */}
            {base === null ? null : <Apoio texto={base} completo={baseDaPrevisao(v)} />}
          </span>
        );
      },
    },
    {
      chave: 'atual',
      rotulo: 'Leitura atual',
      alinhamento: 'numero',
      /* Sai abaixo de 1280 px (R01, p.28): quanto falta já está na Previsão. */
      prioridade: 'secundaria',
      celula: (v) =>
        v.gatilho === 'dias' || v.leituraAtual === null ? (
          <span className="txt-apoio text-tinta-secundaria">—</span>
        ) : (
          <MedidaNaCor valor={formatarDecimal(v.leituraAtual, 1)} unidade={UNIDADE_DO_GATILHO[v.gatilho]} cor={null} />
        ),
    },
  ];

  /* Ações da linha: à direita, só no hover e no foco (p.18) — quem desenha é
     a tabela. Em linha, e não empilhadas. */
  function acoesDaLinha(v: VencimentoDaApi) {
    return (
      <>
        {/* A PREVENTIVA VIRA O.S. por aqui, já com a tarefa do plano: é a
            liberação dela que faz o ciclo andar (ferramenta 4 do 05). */}
        {pode('os.abrir') && (v.estado === 'vencida' || v.estado === 'vence_logo') ? (
          <Botao
            nivel="terciario"
            disabled={abrirOrdem.isPending}
            onClick={() => {
              abrirOrdemDaLinha(v);
            }}
          >
            Abrir O.S.
          </Botao>
        ) : null}
        {/* AGENDAR A PARADA COM A OPERAÇÃO (ferramenta 4 do 05): a O.S.
            aberta ganha a janela em que a máquina sai da frente. */}
        {pode('os.abrir') && v.ordemAberta ? (
          <Botao
            nivel="terciario"
            onClick={() => {
              setProgramando(v);
            }}
          >
            {v.ordemAberta.paradaProgramada ? 'Reprogramar parada' : 'Programar parada'}
          </Botao>
        ) : null}
        {pode('realizacao.registrar') ? (
          <HistoriaNaMesa codigo="BTN-MNT-06" className="flex">
            <Botao
              nivel="terciario"
              onClick={() => {
                setRegistrando(v);
              }}
            >
              Registrar realização
            </Botao>
          </HistoriaNaMesa>
        ) : null}
      </>
    );
  }

  /* SEM `manutencao.ler`, SÓ O ESTADO SEM PERMISSÃO (prova do marco,
     07/10/2026): o operador via "0 de 0 tarefas" nos indicadores ao lado da
     recusa — número que ele não pode ter, escrito como se fosse a fila. */
  if (sessao.data && !pode('manutencao.ler')) {
    return (
      <div className="min-w-0 flex-1 overflow-auto">
        <div className="pagina">
          <CabecalhoPagina sobretitulo="Manutenção" titulo="Planos preventivos e vencimentos" />
          <SemPermissao modulo="a manutenção" perfil={perfilAPedir('manutencao.ler')} />
        </div>
      </div>
    );
  }

  /* MONTAR PLANO É PÁGINA INTEIRA (R01, p.29): com duas tarefas passa de dez
     campos, em duas seções. A lista sai da frente e volta ao gravar ou
     descartar. O preenchimento fica guardado (p.16). */
  if (montando) {
    return (
      <div className="min-w-0 flex-1 overflow-auto">
        <div className="pagina">
          <CabecalhoPagina
            sobretitulo="Manutenção"
            titulo="Montar plano"
            subtitulo="As tarefas do manual do fabricante, o intervalo de cada uma e a página de onde saíram"
          />
          <FormularioDePlano
            aoFechar={() => {
              setMontando(false);
            }}
          />
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="min-w-0 flex-1 overflow-auto">
        <div className="pagina">
          <CabecalhoPagina
            sobretitulo="Manutenção"
            titulo="Planos preventivos e vencimentos"
            subtitulo="O que venceu, o que vence logo, e em que leitura a previsão se apoia"
            acoes={
              /* UMA ação em laranja: montar plano. */
              pode('plano.gerir') ? (
                <Botao
                  nivel="primario"
                  onClick={() => {
                    setPlanoAberto(null);
                    setMontando(true);
                  }}
                >
                  Montar plano
                </Botao>
              ) : null
            }
          />

          <FaixaIndicadores>
            {/* Carregando (p.32): o rótulo e o ícone ficam, e o número vira
                esqueleto — antes, "0" e "de 0 tarefas" no lugar de uma fila
                que ainda não tinha chegado. */}
            <CardIndicador
              rotulo="Vencidas"
              icone={Wrench}
              tom="parada"
              carregando={vencimentos.isPending}
              /* R02: preventiva vencida é o próprio alerta. Zero sai em preto. */
              alerta={fila.vencidas > 0}
              valor={formatarInteiro(fila.vencidas)}
              /* Contexto é a BASE, em frase curta (p.17): em quantas máquinas.
                 A frase longa quebrava em três linhas no card de 1024 px. */
              contexto={
                /* Vazio enquanto a fila vem: o card desenha a barra (p.32). */
                vencimentos.isPending
                  ? ''
                  : fila.maquinasComVencida === 0
                    ? 'em nenhuma máquina'
                    : `em ${formatarInteiro(fila.maquinasComVencida)} ${fila.maquinasComVencida === 1 ? 'máquina' : 'máquinas'}`
              }
              aoAbrir={() => {
                filtrar('vencida', maquina);
              }}
            />
            <CardIndicador
              rotulo="Vencem logo"
              icone={Clock}
              tom="standby"
              carregando={vencimentos.isPending}
              valor={formatarInteiro(fila.venceLogo)}
              /* A base, e não a definição (p.17); a definição vai na linha de
                 ajuda abaixo da faixa. */
              contexto={vencimentos.isPending ? '' : `de ${formatarInteiro(todos.length)} ${todos.length === 1 ? 'tarefa' : 'tarefas'}`}
              aoAbrir={() => {
                filtrar('vence_logo', maquina);
              }}
            />
            {/* Contexto é a BASE, não a definição (p.17): "7 de 9 tarefas". */}
            <CardIndicador
              rotulo="Sem ponto de partida"
              icone={Clock}
              carregando={vencimentos.isPending}
              valor={formatarInteiro(fila.semPontoDePartida)}
              contexto={vencimentos.isPending ? '' : `de ${formatarInteiro(todos.length)} ${todos.length === 1 ? 'tarefa' : 'tarefas'}`}
              {...(todos.length > 0 ? { proporcao: fila.semPontoDePartida / todos.length } : {})}
              aoAbrir={() => {
                filtrar('sem_ponto_de_partida', maquina);
              }}
            />
            {/* "Sem leitura" no lugar de "Planos publicados": o inventário de
                planos está na lista abaixo; esta é a pergunta da manhã — que
                previsão não se faz por falta de horímetro (p.17). */}
            <CardIndicador
              rotulo="Sem leitura"
              icone={Clock}
              carregando={vencimentos.isPending}
              valor={formatarInteiro(fila.semLeitura)}
              contexto={vencimentos.isPending ? '' : `de ${formatarInteiro(todos.length)} ${todos.length === 1 ? 'tarefa' : 'tarefas'}`}
              {...(todos.length > 0 ? { proporcao: fila.semLeitura / todos.length } : {})}
              aoAbrir={() => {
                filtrar('sem_leitura', maquina);
              }}
            />
          </FaixaIndicadores>

          {/* A DEFINIÇÃO do "vence logo", uma vez, fora do card: no contexto do
              card ela quebrava em três linhas e escondia a base (p.17). */}
          <p className="txt-apoio text-pretty text-tinta-secundaria">
            Vence logo a tarefa que já entrou no aviso dela, ou que a projeção põe em até 7 dias.
          </p>

          {/* AS PARADAS DE HOJE (0089), entre os indicadores e o filtro (R06,
              p.70): a O.S. programada com a operação, que a frente precisa ver
              no dia. A mesma agenda vai para a Operação; some sem parada. */}
          <AgendaDeParadas pode={pode} />

          {/* --------------------------------------------------- o filtro */}
          <BarraFiltros
            chips={chips}
            /* Enquanto a fila vem, a contagem vira barra (p.32): "0 de 0
               tarefas" afirmava uma fila vazia. */
            contagem={
              vencimentos.isPending
                ? null
                : `${formatarInteiro(linhas.length)} de ${formatarInteiro(todos.length)} ${todos.length === 1 ? 'tarefa' : 'tarefas'}`
            }
            aoLimpar={() => {
              filtrar('', '');
            }}
          >
            <CampoSelecao
              rotulo="Estado"
              valor={estado}
              rotuloVazio="Todos"
              aplicado={estado !== ''}
              opcoes={ORDEM_DOS_FILTROS.map((e) => ({ valor: e, rotulo: ROTULO_DO_ESTADO[e] }))}
              aoMudar={(novo) => {
                filtrar(ehEstado(novo) ? novo : '', maquina);
              }}
            />
            {vencimentos.isPending ? (
              /* As máquinas saem da própria fila (p.32): enquanto ela vem, o
                 rótulo fica e só o controle vira barra. */
              <div className="w-40">
                <Campo id={idDaMaquina} rotulo="Equipamento">
                  <ControleCarregando oQue="Carregando os equipamentos" />
                </Campo>
              </div>
            ) : (
              <CampoSelecao
                rotulo="Equipamento"
                valor={maquina}
                rotuloVazio="Todos"
                aplicado={maquina !== ''}
                opcoes={comVisualDaFrota(maquinas.map(([id, codigo]) => ({ valor: id, rotulo: codigo, adesivo: { id, codigo } })))}
                aoMudar={(novo) => {
                  filtrar(estado, novo);
                }}
                adesivo={{ porQueFora: 'o filtro mostra só as máquinas com vencimento na fila' }}
              />
            )}
          </BarraFiltros>

          {/* A instrução de "sem ponto de partida", UMA vez, e não repetida em
              cada linha (p.18: segunda linha curta). */}
          {semPontoNaLista > 0 ? (
            <p className="txt-apoio text-pretty text-tinta-secundaria">
              {`${formatarInteiro(semPontoNaLista)} ${semPontoNaLista === 1 ? 'tarefa está' : 'tarefas estão'} sem ponto de partida: use “Registrar realização” na linha para dizer quando a tarefa foi feita pela última vez, e o vencimento passa a contar dela.`}
            </p>
          ) : null}

          <Tabela
            rotulo="Vencimentos por máquina e tarefa"
            colunas={COLUNAS}
            linhas={visiveis}
            alturaDupla
            chaveDaLinha={chaveDe}
            {...(podeAgir ? { acoes: acoesDaLinha } : {})}
            carregando={vencimentos.isPending}
            /* A linha de quem abriu a janela fica marcada (p.19). */
            selecionada={registrando ? chaveDe(registrando) : null}
            erro={
              vencimentos.isError ? (
                <EstadoVazio
                  titulo="Não foi possível carregar os vencimentos"
                  descricao={explicarFalha(vencimentos.error).comoResolver}
                  acao={
                    <Botao
                      nivel="secundario"
                      onClick={() => {
                        void vencimentos.refetch();
                      }}
                    >
                      Carregar de novo
                    </Botao>
                  }
                />
              ) : null
            }
            vazio={
              todos.length > 0 ? (
                <EstadoVazio
                  titulo="Nenhum vencimento com esses filtros"
                  descricao="Troque o estado ou o equipamento para ver as outras tarefas."
                  acao={
                    <Botao
                      nivel="secundario"
                      onClick={() => {
                        filtrar('', '');
                      }}
                    >
                      Limpar filtros
                    </Botao>
                  }
                />
              ) : (
                <EstadoVazio
                  titulo="Nenhum plano publicado alcança uma máquina ainda"
                  descricao="Monte um plano para um modelo, uma configuração ou uma máquina, revise contra o manual e publique. O vencimento aparece aqui para cada máquina que ele alcança."
                />
              )
            }
            rodape={
              <RodapeTabela
                primeiro={inicio + 1}
                ultimo={inicio + visiveis.length}
                total={linhas.length}
                pagina={paginaSegura}
                paginas={paginas}
                porPagina={porPagina}
                opcoesPorPagina={OPCOES_POR_PAGINA}
                aoMudarPagina={(nova) => {
                  irPara({ pagina: nova });
                }}
                aoMudarPorPagina={(novo) => {
                  irPara({ porPagina: novo, pagina: 1 });
                }}
              />
            }
          />

          <PlanosDaManutencao aberto={planoAberto} aoAbrir={setPlanoAberto} />
        </div>
      </div>

      {/* O painel é IRMÃO da área rolável (R01, p.28): fixo ao lado da lista a
          partir de 1280 px, gaveta abaixo disso. `key`: trocar de plano
          descarta o que ficou pela metade no anterior. */}
      {planoAberto ? (
        <PainelDoPlano
          key={planoAberto}
          id={planoAberto}
          podeGerir={pode('plano.gerir')}
          podePublicar={pode('plano.publicar')}
          aoAbrir={setPlanoAberto}
          aoFechar={() => {
            setPlanoAberto(null);
          }}
        />
      ) : null}

      {/* Registrar realização é tarefa curta: MODAL ao centro (R01, p.29), e
          não um cartão no alto da página, longe da linha que o pediu. */}
      <Janela
        titulo={programando?.ordemAberta?.paradaProgramada ? 'Reprogramar parada' : 'Programar parada'}
        {...(programando?.ordemAberta
          ? {
              descricao: `${numeroDaOrdem(programando.ordemAberta.numero)} · ${programando.equipamento} · ${programando.descricao}. A janela em que a máquina sai da operação, combinada com quem responde pela frente.`,
            }
          : {})}
        largura="m"
        aberta={programando?.ordemAberta != null}
        aoFechar={() => {
          setProgramando(null);
        }}
      >
        {programando?.ordemAberta ? (
          <FormularioDaParada
            ordemId={programando.ordemAberta.id}
            atual={programando.ordemAberta.paradaProgramada}
            aoGravar={() => {
              setProgramando(null);
            }}
            aoCancelar={() => {
              setProgramando(null);
            }}
          />
        ) : null}
      </Janela>
      <Janela
        titulo="Registrar realização"
        aberta={registrando !== null}
        aoFechar={() => {
          setRegistrando(null);
        }}
      >
        {registrando ? (
          <FormularioDeRealizacao
            key={chaveDe(registrando)}
            vencimento={registrando}
            aoFechar={() => {
              setRegistrando(null);
            }}
          />
        ) : null}
      </Janela>
    </>
  );
}

/* ========================================================================
 * O PONTO DE PARTIDA
 * ===================================================================== */

type CampoDaRealizacao = 'dia' | 'leitura' | 'evidencia';

/**
 * REGISTRAR REALIZAÇÃO no desenho do lançamento de abastecimento (pedido do
 * dono, 31/10/2026): o dia já vem com hoje; a leitura vem com a última
 * leitura da máquina quando ela é do mesmo dia, e fica na etiqueta ao lado do
 * campo para conferir; corrigir um registro anterior, que é raro, fica atrás
 * de um botão.
 */
function FormularioDeRealizacao({
  vencimento,
  aoFechar,
}: {
  readonly vencimento: VencimentoDaApi;
  readonly aoFechar: () => void;
}) {
  const base = useId();
  const registrar = useRegistrarRealizacao();
  const anteriores = useRealizacoes(vencimento.equipamentoId);
  const medidor = MEDIDOR_DO_GATILHO[vencimento.gatilho];

  const [hoje] = useState(() => diaDaOperacao());
  const [diaDigitado, setDia] = useState<string | null>(null);
  const [leituraDigitada, setLeitura] = useState<string | null>(null);
  const [evidencia, setEvidencia] = useState('');
  const [corrige, setCorrige] = useState('');
  const [corrigindo, setCorrigindo] = useState(false);
  const [erros, setErros] = useState<Readonly<Partial<Record<CampoDaRealizacao, string | null>>>>({});
  const [recusa, setRecusa] = useState<{ erro: string; comoResolver: string } | null>(null);

  /* `null` = a pessoa ainda não mexeu: vale o que o sistema sabe. */
  const dia = diaDigitado ?? hoje;
  const sugerida = medidor ? leituraSugerida(vencimento, dia) : null;
  const leitura = leituraDigitada ?? (sugerida === null ? '' : formatarAteDuasCasas(sugerida));
  const unidade = vencimento.gatilho === 'km' ? 'km' : 'h';

  /* Só as realizações DESTA tarefa, e só as que ainda valem: corrigir uma
     correção é possível; corrigir a mesma linha duas vezes, não. */
  const corrigiveis = (anteriores.data ?? []).filter(
    (r) => r.tarefa === vencimento.descricao && !r.corrigida,
  );

  function checar(campo: CampoDaRealizacao): string | null {
    if (campo === 'dia') {
      if (dia === '') return 'Informe o dia em que a tarefa foi feita.';
      if (dia > diaDaOperacao()) return 'O dia está no futuro. Informe quando a tarefa foi feita.';
      return null;
    }
    if (campo === 'leitura') {
      const valor = leitura.trim() === '' ? null : interpretarNumero(leitura);
      return medidor && valor === null ? `Informe a leitura do ${medidor} no dia da tarefa.` : null;
    }
    return evidencia.trim() === '' ? 'Diga de onde vem a informação: livro da oficina, nota, laudo.' : null;
  }

  /* Valida AO SAIR do campo (p.16) e limpa enquanto a pessoa corrige. */
  function sair(campo: CampoDaRealizacao) {
    setErros((atual) => ({ ...atual, [campo]: checar(campo) }));
  }
  function limpar(campo: CampoDaRealizacao) {
    setErros((atual) => ({ ...atual, [campo]: null }));
  }

  function enviar(evento: FormEvent) {
    evento.preventDefault();
    const achados = { dia: checar('dia'), leitura: checar('leitura'), evidencia: checar('evidencia') };
    setErros(achados);
    if (achados.dia || achados.leitura || achados.evidencia) return;
    const valor = leitura.trim() === '' ? null : interpretarNumero(leitura);
    setRecusa(null);
    registrar.mutate(
      {
        equipamentoId: vencimento.equipamentoId,
        tarefaId: vencimento.tarefaId,
        realizadaEm: new Date(`${dia}T12:00:00`).toISOString(),
        leitura: medidor ? valor : null,
        evidencia: evidencia.trim(),
        corrigeId: corrige === '' ? null : corrige,
      },
      {
        onSuccess: aoFechar,
        onError: (falha) => {
          setRecusa(explicarFalha(falha));
        },
      },
    );
  }

  const diaDoSistema = diaDigitado === null;
  const leituraDoSistema = leituraDigitada === null && sugerida !== null;

  return (
    <form onSubmit={enviar} noValidate className="flex flex-col gap-6">
      <p className="txt-apoio text-pretty text-tinta-secundaria">
        <span className="text-tinta">{`${vencimento.descricao} em ${vencimento.equipamento}. `}</span>
        {vencimento.estado === 'sem_ponto_de_partida'
          ? 'Ainda não há ponto de partida: informe a última vez que a tarefa foi feita, e o vencimento passa a contar dela.'
          : 'A realização não se edita: um registro errado se corrige com outro.'}
      </p>

      {/* Só a recusa do servidor mora aqui, no topo (p.22); o erro de campo vai abaixo dele. */}
      {recusa === null ? null : (
        <div role="alert" className="flex flex-col gap-1 rounded-controle border border-borda bg-rebaixada p-4">
          <span className="txt-corpo text-tinta">{recusa.erro}</span>
          <span className="txt-apoio text-tinta-secundaria">{recusa.comoResolver}</span>
        </div>
      )}

      <div className="formulario-grade">
        <Campo
          id={`${base}-dia`}
          rotulo="Feita em"
          preenchido={diaDoSistema}
          erro={erros.dia ?? null}
          {...(diaDoSistema ? { ajuda: 'Hoje. Troque se foi antes.' } : {})}
        >
          <input
            id={`${base}-dia`}
            type="date"
            className="controle w-full"
            value={dia}
            max={hoje}
            onChange={(e) => {
              setDia(e.target.value);
              limpar('dia');
            }}
            onBlur={() => {
              sair('dia');
            }}
            {...(erros.dia ? { 'aria-invalid': true, 'aria-describedby': `${base}-dia-erro` } : {})}
          />
        </Campo>
        {medidor ? (
          /* A ÚLTIMA LEITURA AO LADO DO CAMPO (decisão 54), como no abastecimento. */
          <Campo
            id={`${base}-leitura`}
            rotulo={`Leitura do ${medidor}`}
            preenchido={leituraDoSistema}
            erro={erros.leitura ?? null}
            {...(vencimento.leituraAtual !== null
              ? {
                  etiqueta: (
                    <EtiquetaDaUltimaLeitura
                      leitura={{
                        valor: vencimento.leituraAtual,
                        em: vencimento.leituraAtualEm ? new Date(vencimento.leituraAtualEm) : null,
                        doAparelho: false,
                      }}
                      unidade={unidade}
                    />
                  ),
                }
              : {})}
            {...(leituraDoSistema ? { ajuda: `A última leitura de ${vencimento.equipamento}, de hoje. Troque se a tarefa foi feita em outra.` } : {})}
          >
            <div className="flex items-center gap-2">
              <input
                id={`${base}-leitura`}
                type="text"
                inputMode="decimal"
                autoComplete="off"
                className="controle w-full"
                value={leitura}
                onChange={(e) => {
                  setLeitura(e.target.value);
                  limpar('leitura');
                }}
                onBlur={() => {
                  sair('leitura');
                }}
                {...(erros.leitura ? { 'aria-invalid': true, 'aria-describedby': `${base}-leitura-erro` } : {})}
              />
              <span className="txt-corpo shrink-0 text-tinta-apagada">{unidade}</span>
            </div>
          </Campo>
        ) : null}
        <div className="campo-linha">
          <Campo id={`${base}-evidencia`} rotulo="De onde vem esta informação" erro={erros.evidencia ?? null}>
            <input
              id={`${base}-evidencia`}
              type="text"
              autoComplete="off"
              className="controle w-full"
              placeholder="Livro da oficina, folha 12"
              value={evidencia}
              onChange={(e) => {
                setEvidencia(e.target.value);
                limpar('evidencia');
              }}
              onBlur={() => {
                sair('evidencia');
              }}
              {...(erros.evidencia ? { 'aria-invalid': true, 'aria-describedby': `${base}-evidencia-erro` } : {})}
            />
          </Campo>
        </div>

        {/* CORRIGIR É RARO: o campo fica atrás do botão, e o botão só existe
            quando há registro desta tarefa para corrigir. Escolhido um, o
            bloco fica aberto. */}
        {anteriores.isPending ? (
          /* Os registros anteriores ainda vêm (p.32): sem eles não se sabe se
             há o que corrigir, e o campo esperando diz isso. */
          <div className="campo-linha">
            <Campo id={`${base}-corrige`} rotulo="Corrige" opcional>
              <ControleCarregando oQue="Carregando os registros anteriores" />
            </Campo>
          </div>
        ) : corrigiveis.length > 0 ? (
          /* Com um registro já escolhido, o bloco fica aberto: quem escolheu
             não perde de vista o que vai ser corrigido. */
          <BotaoDoRaro
            aberto={corrigindo || corrige !== ''}
            aoAlternar={() => {
              setCorrigindo((aberto) => !aberto);
            }}
            rotulo="Corrigir um registro anterior"
            rotuloAberto="Fechar correção"
            idDoConteudo={`${base}-correcao`}
          />
        ) : null}
        {(corrigindo || corrige !== '') && corrigiveis.length > 0 ? (
          <div id={`${base}-correcao`} className="campo-linha bloco-rebaixado p-4">
            <Campo
              id={`${base}-corrige`}
              rotulo="Corrige"
              opcional
              ajuda="O novo registro passa a valer no lugar do escolhido. O antigo fica no histórico."
            >
              <ListaDeEscolha
                id={`${base}-corrige`}
                rotulo="Corrige"
                valor={corrige}
                aoMudar={setCorrige}
                rotuloVazio="Nenhum registro anterior"
                opcoes={corrigiveis.map((r) => ({
                  valor: r.id,
                  rotulo: formatarData(new Date(r.realizadaEm)),
                  detalhe: [r.leitura === null ? null : `${formatarAteDuasCasas(r.leitura)} ${unidade}`, r.evidencia].filter(Boolean).join(' · '),
                }))}
              />
            </Campo>
          </div>
        ) : null}
      </div>

      {/* Título e botão dizem a mesma coisa: "Registrar realização". Primário
          à direita, o de sair como terciário à esquerda dele (p.15, p.29). */}
      <div className="barra-de-acoes">
        <Botao nivel="terciario" type="button" onClick={aoFechar}>
          Cancelar registro
        </Botao>
        <Botao nivel="primario" type="submit" disabled={registrar.isPending}>
          {registrar.isPending ? 'Registrando…' : 'Registrar realização'}
        </Botao>
      </div>
    </form>
  );
}
